import React from 'react'
import { PopupMenu, IconButton, Typography, Tooltip, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import classnames from 'classnames'
import {
  BUILDER_V2_INK,
  BUILDER_V2_SELECTED_BACKGROUND,
  BUILDER_V2_ICON_BUTTON,
  BUILDER_V2_ICON_BUTTON_SELECTED
} from '../../util'

interface ButtonSelectOptionProps {
  label: string
  value: string | number | boolean
  buttons: {
    icon: React.ReactNode
    value: string | number | boolean
    disabled?: boolean
    forceSelected?: boolean
  }[]
  onChange: (value: string | number | boolean) => void
  infoText?: string
  classes?: StyleClasses<typeof useStyles>
}

const useStyles = makeStyles<Theme, ButtonSelectOptionProps>(
  (theme: Theme) => {
    return {
      root: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: 44
      },
      label: {
        fontSize: '.8125rem',
        fontWeight: 500,
        marginRight: theme.spacing(3),
        color: BUILDER_V2_INK
      },
      button: BUILDER_V2_ICON_BUTTON,
      selected: BUILDER_V2_ICON_BUTTON_SELECTED,
      forceSelected: {
        '&.Mui-disabled': {
          background: BUILDER_V2_SELECTED_BACKGROUND,
          color: BUILDER_V2_INK
        }
      },
      tooltipRoot: {
        marginRight: 'auto',
        marginLeft: theme.spacing(-1)
      },
      tooltipBadge: {
        cursor: 'pointer',
        top: 0,
        right: 0,
        '& svg': {
          fontSize: '1rem'
        }
      },
      dropdownToggle: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        '&:hover': {
          '& $dropdownToggleIcon': {
            color: theme.palette.secondary.main
          }
        }
      },
      dropdownToggleIcon: {},
      buttonGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }
    }
  },
  { name: 'ButtonSelectOption' }
)

export default function ButtonSelectOption(props: ButtonSelectOptionProps) {
  const classes = useStyles(props)
  const { label, value, onChange, buttons, infoText } = props

  return (
    <div className={classes.root}>
      <Typography className={classes.label} variant={'h5'}>
        {label}
      </Typography>
      {infoText && (
        <div className={classes.tooltipRoot}>
          <Tooltip
            placement={'left'}
            content={infoText}
            icon={<InfoOutlinedIcon />}
            classes={{ badge: classes.tooltipBadge }}
          />
        </div>
      )}
      <div className={classes.buttonGroup}>
        {buttons.length < 9 ? (
          buttons.map((button, index) => {
            const selected = value === button.value
            return (
              <IconButton
                key={index}
                onClick={handleButtonClick(button.value)}
                classes={{
                  root: classnames({
                    [classes.button]: true,
                    [classes.selected]: selected || button.forceSelected,
                    [classes.forceSelected]: button.forceSelected
                  })
                }}
                disabled={button.disabled}
              >
                {button.icon}
              </IconButton>
            )
          })
        ) : (
          <PopupMenu
            component={
              <div className={classes.dropdownToggle}>
                <IconButton
                  classes={{
                    root: classnames({ [classes.button]: true, [classes.selected]: true })
                  }}
                >
                  {buttons.find((button) => button.value === value)?.icon}
                </IconButton>
                <ArrowDropDownIcon className={classes.dropdownToggleIcon} />
              </div>
            }
          >
            {buttons.map((button, index) => {
              const selected = value === button.value
              return (
                <IconButton
                  key={index}
                  onClick={handleButtonClick(button.value)}
                  classes={{
                    root: classnames({
                      [classes.button]: true,
                      [classes.selected]: selected || button.forceSelected,
                      [classes.forceSelected]: button.forceSelected
                    })
                  }}
                  disabled={button.disabled}
                >
                  {button.icon}
                </IconButton>
              )
            })}
          </PopupMenu>
        )}
      </div>
    </div>
  )

  function handleButtonClick(value: string | number | boolean) {
    return (_ev: React.MouseEvent) => {
      onChange(value)
    }
  }
}
